/**
 * Defensives (PLAN §7):
 *   tank  - active mitigation when a tank-buster cast on me starts; the big cooldown
 *           below 35% HP or for a boss buster; spend AM charges rather than cap them.
 *   dps   - personal defensive below 30% HP.
 *   healer- personal immunity below 25% HP.
 * +encounters (docs/design/encounters.md §5 C8/C10, C5):
 *   tank  - mitigation at high stacks of a stacking debuff (Septic Bite), or medium stacks with a
 *           buster that scales off them due within 4 s (read from the caster's `ai.timers`, like a
 *           boss mod); mitigation when an enraged enemy is hitting me.
 *   all   - personal defensive when an exploding debuff about to go off would take a big chunk of
 *           my HP, or when a stacking poison is high and I'm hurt.
 */
import type { AbilityId, AuraDef, Command, DeepReadonly, EffectSpec } from '@keystone/sim';
import type { ClassKit } from '../kits';
import { noticed, type BotMemory } from '../memory';
import { abilityLocked, castTargetId, charges, hasAura, hpPct, type Perception, type RUnit } from '../perception';

/** Enemies casting a tank-buster at `targetId` that this bot has noticed. */
export function bustersOn(p: Perception, targetId: string, reactionMs: number): RUnit[] {
  return p.engaged.filter(
    (e) => e.cast && p.now >= e.cast.startedAt + reactionMs && castTargetId(e) === targetId && p.view.content.enemyAbility(e.cast.abilityId)?.kind === 'tankbuster',
  );
}

export interface DefensivePlan {
  cmds: Command[];
  /** True if every cast in the plan is off the GCD (the bot may keep acting this think). */
  offGcd: boolean;
}

function use(p: Perception, abilityId: AbilityId | undefined): DefensivePlan | null {
  if (!abilityId) return null;
  const ab = p.view.content.ability(abilityId);
  // +rogue-specs: an enemy-targeted mitigation (Mirage's Sidestep) goes at the current target.
  const targetId = ab?.targeting === 'enemy' ? p.self.targetId : p.self.id;
  if (!ab || !targetId || abilityLocked(p.view, p.self, abilityId) || !p.view.queries.canCast(p.self.id, abilityId, targetId, null).ok) return null;
  return { cmds: [{ type: 'cast', actorId: p.self.id, abilityId, targetId }], offGcd: ab.gcdMs === 0 };
}

export function planDefensive(p: Perception, kit: ClassKit, mem: BotMemory): DefensivePlan | null {
  const self = p.self;
  if (p.engaged.length === 0) return null;
  const hp = hpPct(self);

  if (kit.role === 'tank') {
    const amUp = kit.activeMitigationAura ? hasAura(self, kit.activeMitigationAura) : false;
    const majorUp = kit.majorDefensiveAura ? hasAura(self, kit.majorDefensiveAura) : false;
    const busters = bustersOn(p, self.id, mem.skill.reactionMs);
    const low = noticed(mem, 'def:tankLow', hp < 0.35, p.now);
    if (busters.length > 0) {
      const bigHit = busters.some((b) => b.tier === 'boss' || b.tier === 'miniboss');
      if (!amUp && !majorUp) {
        const am = use(p, kit.activeMitigation);
        if (am) return am;
      }
      if (!majorUp && (bigHit || hp < 0.5) && !amUp) return use(p, kit.majorDefensive);
    }
    // +encounters: stacking tank debuffs and enraged attackers.
    if (!amUp && !majorUp && (tankStackRisk(p, mem) || enragedOnMe(p, mem))) {
      const am = use(p, kit.activeMitigation);
      if (am) return am;
      if (hp < 0.5) {
        const major = use(p, kit.majorDefensive);
        if (major) return major;
      }
    }
    if (low && !majorUp) {
      const major = use(p, kit.majorDefensive);
      if (major) return major;
      if (!amUp) return use(p, kit.activeMitigation);
    }
    // Don't sit on capped charges while taking damage.
    const amDef = kit.activeMitigation ? p.view.content.ability(kit.activeMitigation) : undefined;
    if (amDef && !amUp && hp < 0.8 && charges(self, amDef) >= (amDef.cooldown?.charges ?? 1) && (amDef.cooldown?.charges ?? 1) > 1) {
      return use(p, kit.activeMitigation);
    }
    return null;
  }

  const threshold = kit.role === 'healer' ? 0.25 : 0.3;
  if (noticed(mem, 'def:low', hp < threshold, p.now)) return use(p, kit.personalDefensive);
  // +encounters: a 'deadly' boss cast is about to land and I'm already hurt.
  const deadly = p.engaged.some((e) => e.cast && e.cast.endsAt - p.now < 1500 && p.now >= e.cast.startedAt + mem.skill.reactionMs && p.view.content.enemyAbility(e.cast.abilityId)?.warning?.severity === 'deadly');
  if (deadly && hp < 0.6) return use(p, kit.personalDefensive);
  // +encounters: an exploding debuff about to go off, or a stacking poison while hurt.
  if (explosionRisk(p, mem) || poisonRisk(p, mem, hp)) return use(p, kit.personalDefensive);
  return null;
}

// ---------------------------------------------------------------------------
// +encounters: debuff risk checks
// ---------------------------------------------------------------------------

/** Stacks at which a stacking tank debuff alone calls for mitigation (fraction of max stacks). */
const TANK_STACKS_HIGH = 0.75;
/** Stacks at which an upcoming scaling buster calls for mitigation. */
const TANK_STACKS_MED = 0.5;
/** How far ahead a scaling buster is anticipated. */
const BUSTER_LOOKAHEAD_MS = 4000;
/** An exploding debuff this close to expiry with damage >= EXPLODE_HP_FRAC of current HP. */
const EXPLODE_WINDOW_MS = 2000;
const EXPLODE_HP_FRAC = 0.5;
/** Stacking poison: stacks >= this fraction of max while below POISON_HP. */
const POISON_STACKS = 0.75;
const POISON_HP = 0.6;

type RAura = RUnit['auras'][number];

function aDef(p: Perception, id: string): DeepReadonly<AuraDef> | undefined {
  return p.view.content.aura(id);
}

/** Visible harmful stacking debuffs on me (after reaction time). */
function stackingDebuffs(p: Perception, mem: BotMemory): { a: RAura; def: DeepReadonly<AuraDef>; frac: number }[] {
  const out: { a: RAura; def: DeepReadonly<AuraDef>; frac: number }[] = [];
  for (const a of p.self.auras) {
    const def = aDef(p, a.auraId);
    if (!def || def.kind !== 'debuff' || def.stacking !== 'stack' || !def.maxStacks || def.maxStacks < 3) continue;
    if (p.now < a.appliedAt + mem.skill.reactionMs) continue;
    out.push({ a, def, frac: a.stacks / def.maxStacks });
  }
  return out;
}

/** Does this effect list scale with stacks of `auraId` on the target? */
function scalesWith(effects: readonly DeepReadonly<EffectSpec>[], auraId: string): boolean {
  return effects.some((e) => e.type === 'damage' && (e.amount as { perStack?: { auraId: string } }).perStack?.auraId === auraId);
}

function tankStackRisk(p: Perception, mem: BotMemory): boolean {
  for (const { a, frac } of stackingDebuffs(p, mem)) {
    if (frac >= TANK_STACKS_HIGH) return true;
    if (frac < TANK_STACKS_MED) continue;
    for (const e of p.engaged) {
      // A buster that scales with these stacks: being cast at me now, or due soon on its timer.
      if (e.cast && castTargetId(e) === p.self.id) {
        const ab = p.view.content.enemyAbility(e.cast.abilityId);
        if (ab && scalesWith(ab.effects, a.auraId)) return true;
      }
      const timers = e.ai?.timers;
      if (!timers || castTargetId(e) !== p.self.id) continue;
      for (const id of Object.keys(timers)) {
        const due = timers[id]!;
        if (due - p.now > BUSTER_LOOKAHEAD_MS) continue;
        const ab = p.view.content.enemyAbility(id);
        if (ab?.kind === 'tankbuster' && scalesWith(ab.effects, a.auraId)) return true;
      }
    }
  }
  return false;
}

function enragedOnMe(p: Perception, mem: BotMemory): boolean {
  return p.engaged.some(
    (e) => e.targetId === p.self.id && e.auras.some((a) => p.now >= a.appliedAt + mem.skill.reactionMs && aDef(p, a.auraId)?.dispelType === 'enrage'),
  );
}

/** Rough explosion damage to me (holder) from an explodeOnExpire debuff. */
export function explosionDamage(p: Perception, a: RAura): number {
  const spec = aDef(p, a.auraId)?.explodeOnExpire;
  if (!spec?.damage || spec.hitsHolder === false) return 0;
  const amt = spec.damage.amount;
  const caster = p.world.units[a.casterId];
  const base = (amt.base ?? 0) + (amt.coef ?? 0) * (caster?.stats.power ?? 0);
  return base * (spec.damage.perStack ? a.stacks : 1) * (caster?.scaling.damage ?? 1) + (amt.pctTargetMaxHp ?? 0) * p.self.maxHp;
}

function explosionRisk(p: Perception, mem: BotMemory): boolean {
  for (const a of p.self.auras) {
    if (a.expiresAt === null) continue;
    const left = a.expiresAt - p.now;
    if (left < 0 || left > EXPLODE_WINDOW_MS + mem.skill.reactionMs) continue;
    if (explosionDamage(p, a) >= EXPLODE_HP_FRAC * p.self.hp) return true;
  }
  return false;
}

function poisonRisk(p: Perception, mem: BotMemory, hp: number): boolean {
  if (hp >= POISON_HP) return false;
  return stackingDebuffs(p, mem).some(({ def, frac }) => !!def.periodic && frac >= POISON_STACKS);
}
